import { NgModule, inject } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AppShellComponent } from './core/app-shell/app-shell.component';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard } from './core/guards/admin.guard';
import { moduloGuard } from './core/guards/modulo.guard';
import { sinDineroGuard } from './core/guards/sin-dinero.guard';
import { AuthService } from './core/services/auth.service';

const routes: Routes = [
  {
    path: 'auth/login',
    loadComponent: () => import('./pages/auth/login/login.component').then(c => c.LoginComponent),
  },
  {
    path: 'auth/forgot-password',
    loadComponent: () => import('./pages/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },
  {
    // Ruta pública (sin authGuard) para que el cliente apruebe/rechace items vía link/QR
    path: 'cliente/cotizacion/:token',
    loadComponent: () => import('./pages/cliente-cotizacion/cliente-cotizacion.component').then(c => c.ClienteCotizacionComponent),
  },
  {
    // Ruta pública (sin authGuard) del portal de flotilla: el cliente flotillero
    // consulta el historial 360° de sus unidades con enlace + PIN. Solo lectura.
    path: 'flotilla/portal/:token',
    loadComponent: () => import('./pages/flotilla-portal/flotilla-portal.component').then(c => c.FlotillaPortalComponent),
  },
  {
    // Ruta pública para Términos y Condiciones de Uso y Contratación
    path: 'terminos-y-condiciones',
    loadComponent: () => import('./pages/terminos-condiciones/terminos-condiciones.component').then(c => c.TerminosCondicionesComponent),
  },
  {
    path: '',
    component: AppShellComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: () => inject(AuthService).rutaInicial(), pathMatch: 'full' },
      { path: 'dashboard', canActivate: [sinDineroGuard],        loadComponent: () => import('./pages/dashboard/dashboard.component').then(c => c.DashboardComponent) },
      { path: 'clientes',         loadComponent: () => import('./pages/clientes/clientes.component').then(c => c.ClientesComponent) },
      { path: 'vehiculos',        loadComponent: () => import('./pages/vehiculos/vehiculos.component').then(c => c.VehiculosComponent) },
      // Flotillas muestra pipeline, crédito y montos facturables: fuera del alcance del MECANICO.
      { path: 'flotillas', canActivate: [sinDineroGuard],        loadComponent: () => import('./pages/flotillas/flotillas.component').then(c => c.FlotillasComponent) },
      { path: 'inventario', canActivate: [sinDineroGuard],       loadComponent: () => import('./pages/inventario/inventario.component').then(c => c.InventarioComponent) },
      { path: 'ordenes-servicio', loadComponent: () => import('./pages/ordenes-servicio/ordenes-servicio.component').then(c => c.OrdenesServicioComponent) },
      { path: 'cotizaciones',     loadComponent: () => import('./pages/cotizaciones/cotizaciones.component').then(c => c.CotizacionesComponent) },
      { path: 'punto-venta', canActivate: [sinDineroGuard],      loadComponent: () => import('./pages/punto-venta/punto-venta.component').then(c => c.PuntoVentaComponent) },
      { path: 'cortes-caja', canActivate: [sinDineroGuard],      loadComponent: () => import('./pages/cortes-caja/cortes-caja.component').then(c => c.CortesCajaComponent) },
      { path: 'citas',            loadComponent: () => import('./pages/citas/citas.component').then(c => c.CitasComponent) },
      { 
        path: 'facturacion', 
        canActivate: [sinDineroGuard, adminGuard, moduloGuard],
        data: { modulo: 'facturacion' },
        loadComponent: () => import('./pages/facturacion/facturacion.component').then(c => c.FacturacionComponent),
        children: [
          { path: '', redirectTo: 'configuracion-csd', pathMatch: 'full' },
          { path: 'configuracion-csd', loadComponent: () => import('./pages/facturacion/configura-csd/configura-csd.component').then(c => c.ConfiguraCsdComponent) },
          { path: 'lista-facturas', loadComponent: () => import('./pages/facturacion/lista-facturas/lista-facturas.component').then(c => c.ListaFacturasComponent) }
        ] 
      },
      {
        path: 'contabilidad', canActivate: [sinDineroGuard],
        loadComponent: () => import('./pages/contabilidad/contabilidad.component').then(c => c.ContabilidadComponent),
        children: [
          { path: '', redirectTo: 'resumen', pathMatch: 'full' },
          { path: 'resumen',            loadComponent: () => import('./pages/contabilidad/resumen/resumen.component').then(c => c.ResumenComponent) },
          { path: 'operacion',          loadComponent: () => import('./pages/contabilidad/operacion/operacion.component').then(c => c.OperacionComponent) },
          { path: 'concentrado',        loadComponent: () => import('./pages/contabilidad/concentrado/concentrado.component').then(c => c.ConcentradoComponent) },
          { path: 'gastos-fijos',       loadComponent: () => import('./pages/contabilidad/gastos-fijos/gastos-fijos.component').then(c => c.GastosFijosComponent) },
          { path: 'gastos-variables',   loadComponent: () => import('./pages/contabilidad/gastos-variables/gastos-variables.component').then(c => c.GastosVariablesComponent) },
          { path: 'impuestos',          loadComponent: () => import('./pages/contabilidad/impuestos/impuestos.component').then(c => c.ImpuestosComponent) },
          { path: 'cobranza',           loadComponent: () => import('./pages/contabilidad/cobranza/cobranza.component').then(c => c.CobranzaComponent) },
          { path: 'compras',            loadComponent: () => import('./pages/contabilidad/compras/compras.component').then(c => c.ComprasComponent) },
          { path: 'cxp',                loadComponent: () => import('./pages/contabilidad/cxp/cxp.component').then(c => c.CxpComponent) },
          { path: 'inventario-valuado', loadComponent: () => import('./pages/contabilidad/inventario-valuado/inventario-valuado.component').then(c => c.InventarioValuadoComponent) },
          { path: 'por-costear',        loadComponent: () => import('./shared/ui/costos-pendientes.component').then(c => c.CostosPendientesComponent) },
        ],
      },
      // Pendientes que avisa la campana: clientes por contactar y órdenes terminadas sin cerrar.
      { path: 'recordatorios',    loadComponent: () => import('./pages/recordatorios/recordatorios.component').then(c => c.RecordatoriosComponent) },
      { path: 'ordenes-por-cerrar', canActivate: [sinDineroGuard], loadComponent: () => import('./pages/ordenes-por-cerrar/ordenes-por-cerrar.component').then(c => c.OrdenesPorCerrarComponent) },
      { path: 'proveedores',      loadComponent: () => import('./pages/proveedores/proveedores.component').then(c => c.ProveedoresComponent) },
      { path: 'tecnicos',         redirectTo: 'usuarios', pathMatch: 'full' },
      { path: 'reportes', canActivate: [sinDineroGuard],         loadComponent: () => import('./pages/reportes/reportes.component').then(c => c.ReportesComponent) },
      {
        path: 'kpis',
        canActivate: [sinDineroGuard, moduloGuard],
        data: { modulo: 'kpis' },
        loadComponent: () => import('./pages/kpis/kpis.component').then(c => c.KpisComponent)
      },
      { path: 'usuarios',         loadComponent: () => import('./pages/usuarios/usuarios.component').then(c => c.UsuariosComponent) },
      { path: 'sucursales',       loadComponent: () => import('./pages/sucursales/sucursales.component').then(c => c.SucursalesComponent) },
      { path: 'taller',           loadComponent: () => import('./pages/talleres/talleres.component').then(c => c.TalleresComponent) },
      { path: 'clientes-facturas', canActivate: [adminGuard], loadComponent: () => import('./pages/clientes-facturas/clientes-facturas.component').then(c => c.ClientesFacturasComponent) },
      { path: 'configuracion',    loadComponent: () => import('./pages/configuracion/configuracion.component').then(c => c.ConfiguracionComponent) },
      // Comunicación con la plataforma: abiertas a cualquier rol (el backend decide qué ve cada quien).
      { path: 'novedades',        loadComponent: () => import('./pages/novedades/novedades.component').then(c => c.NovedadesComponent) },
      { path: 'buzon',            loadComponent: () => import('./pages/buzon/buzon.component').then(c => c.BuzonComponent) },
      { path: 'pagos', canActivate: [sinDineroGuard],            loadComponent: () => import('./pages/pagos/pagos.component').then(c => c.PagosComponent) },
    ],
  },
  { path: '**', redirectTo: () => inject(AuthService).rutaInicial() },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
